import { useRef, useEffect, useCallback } from 'react';
import {
    ASSIGNMENT_TRACK_SNIPPET_CAP,
    ASSIGNMENT_TRACK_CLIP_MAX,
    ASSIGNMENT_TRACKING_SYNC_INTERVAL_MS,
    buildInitialTrackingState,
    getAssignmentTrackingStorageKey,
    readAssignmentTrackingLocal,
    writeAssignmentTrackingLocal,
    snapshotMetaDataForApi,
    truncateAssignmentTrackText,
    pushLimitedSnippet,
    getResumeOnceSessionStorageKey,
    mergeSnippetLists,
    getRetakeInitSessionStorageKey,
    buildResetTrackingStateForRetakeAttempt,
    getServerMetaDataFingerprint,
} from 'utils/assignmentSessionTracking';
import * as Sentry from '@sentry/react';
import { syncAssignmentActivityMeta } from 'utils/syncAssignmentActivityMeta';

function isSubmissionCompleteValue(submissionComplete) {
    return (
        submissionComplete === true ||
        submissionComplete === 1 ||
        submissionComplete === '1' ||
        (typeof submissionComplete === 'string' &&
            String(submissionComplete).toLowerCase() === 'true')
    );
}

export function useAssignmentSessionTracking({
    enabled,
    assignmentId,
    userId,
    classId,
    submissionId,
    serverMetaData,
    resumeContext,
    submissionComplete,
    completedSubmissionsCount,
    sessionEnded = false,
    onLogIdSynced,
    getExtraMeta,
    onBeforeFlush,
}) {
    const stateRef = useRef(null);
    if (!stateRef.current) {
        stateRef.current = buildInitialTrackingState(null, null);
    }
    const initSigRef = useRef('');
    const persistThrottleRef = useRef(null);
    const flushThrottleRef = useRef(null);
    const retakeLocalMarkerRef = useRef(null);
    const syncInFlightRef = useRef(false);
    const flushQueueRef = useRef(Promise.resolve(null));
    const internalSessionEndedRef = useRef(false);
    const enabledRef = useRef(enabled);
    const submissionIdRef = useRef(submissionId);
    const sessionEndedRef = useRef(sessionEnded);
    const submissionCompleteRef = useRef(submissionComplete);
    const onLogIdSyncedRef = useRef(onLogIdSynced);
    const getExtraMetaRef = useRef(getExtraMeta);
    const onBeforeFlushRef = useRef(onBeforeFlush);

    enabledRef.current = enabled;
    submissionIdRef.current = submissionId;
    sessionEndedRef.current = sessionEnded;
    submissionCompleteRef.current = submissionComplete;
    onLogIdSyncedRef.current = onLogIdSynced;
    getExtraMetaRef.current = getExtraMeta;
    onBeforeFlushRef.current = onBeforeFlush;

    const subCount = parseInt(completedSubmissionsCount, 10) || 0;
    const submissionCompleteTruthy = isSubmissionCompleteValue(submissionComplete);
    const trackingTerminated =
        sessionEnded || internalSessionEndedRef.current || submissionCompleteTruthy;

    const isTrackingTerminated = useCallback(() => {
        return (
            sessionEndedRef.current ||
            internalSessionEndedRef.current ||
            isSubmissionCompleteValue(submissionCompleteRef.current)
        );
    }, []);
    const isRetakeAttemptInProgress =
        Boolean(submissionId) && !submissionCompleteTruthy && subCount >= 1;

    const persistPartial = useCallback(() => {
        if (!assignmentId || !userId || !classId) return;
        const key = getAssignmentTrackingStorageKey(assignmentId, userId, classId, submissionId);
        const s = stateRef.current;
        const payload = {
            duration_sec: s.duration_sec,
            resume_count: s.resume_count,
            finish_later_count: s.finish_later_count,
            page_visibility_change_count: s.page_visibility_change_count,
            copy_attempt_count: s.copy_attempt_count,
            paste_attempt_count: s.paste_attempt_count,
            copy_content_snippets: s.copy_content_snippets,
            paste_content_snippets: s.paste_content_snippets,
            context_menu_attempt_count: s.context_menu_attempt_count,
            retake_sessions_count: s.retake_sessions_count,
            window_blur_count: s.window_blur_count,
            assignment_session_started_at: s.assignment_session_started_at,
        };
        if (retakeLocalMarkerRef.current != null) {
            payload.retake_track_submissions = retakeLocalMarkerRef.current;
        }
        writeAssignmentTrackingLocal(key, payload);
    }, [assignmentId, userId, classId, submissionId]);

    const schedulePersist = useCallback(() => {
        if (persistThrottleRef.current) return;
        persistThrottleRef.current = setTimeout(() => {
            persistThrottleRef.current = null;
            persistPartial();
        }, 400);
    }, [persistPartial]);

    const flushToServer = useCallback((options = {}) => {
        const { force = false } = options;
        const sid = submissionIdRef.current;
        if (!enabledRef.current || !sid) {
            return Promise.resolve(null);
        }
        if (!force && isTrackingTerminated()) {
            return Promise.resolve(null);
        }
        const run = async () => {
            syncInFlightRef.current = true;
            try {
                if (typeof onBeforeFlushRef.current === 'function') {
                    await onBeforeFlushRef.current();
                }
                let extra = {};
                try {
                    extra =
                        typeof getExtraMetaRef.current === 'function' ? getExtraMetaRef.current() : {};
                } catch (extraError) {
                    Sentry.captureException(extraError);
                }
                const meta = {
                    ...snapshotMetaDataForApi(stateRef.current),
                    ...(extra && typeof extra === 'object' ? extra : {}),
                };
                const logId = await syncAssignmentActivityMeta(sid, meta);
                if (logId && typeof onLogIdSyncedRef.current === 'function') {
                    onLogIdSyncedRef.current(logId);
                }
                return logId;
            } catch (error) {
                Sentry.captureException(error);
                return null;
            } finally {
                syncInFlightRef.current = false;
            }
        };
        flushQueueRef.current = flushQueueRef.current.then(() => run()).catch((error) => {
            Sentry.captureException(error);
            return null;
        });
        return flushQueueRef.current;
    }, [isTrackingTerminated]);

    const scheduleFlushToServer = useCallback(() => {
        if (!submissionIdRef.current || isTrackingTerminated()) return;
        if (flushThrottleRef.current) return;
        flushThrottleRef.current = setTimeout(() => {
            flushThrottleRef.current = null;
            flushToServer();
        }, 600);
    }, [flushToServer, isTrackingTerminated]);

    useEffect(() => {
        if (sessionEnded || submissionCompleteTruthy) {
            internalSessionEndedRef.current = true;
        }
    }, [sessionEnded, submissionCompleteTruthy]);

    useEffect(() => {
        if (submissionCompleteTruthy || subCount < 1) {
            retakeLocalMarkerRef.current = null;
        }
    }, [submissionCompleteTruthy, subCount]);

    useEffect(() => {
        if (!enabled || !assignmentId || !userId || !classId) return;
        const baseSig = `${assignmentId}|${submissionId || 'new'}`;
        const serverMetaFp = getServerMetaDataFingerprint(serverMetaData);
        const extendedSig = `${baseSig}|${submissionCompleteTruthy ? '1' : '0'}|${subCount}|${serverMetaFp}`;
        if (initSigRef.current === extendedSig) return;

        if (isRetakeAttemptInProgress && submissionId) {
            try {
                const retakeSk = getRetakeInitSessionStorageKey(assignmentId, submissionId, subCount);
                if (sessionStorage.getItem(retakeSk)) {
                    const key = getAssignmentTrackingStorageKey(assignmentId, userId, classId, submissionId);
                    const local = readAssignmentTrackingLocal(key);
                    if (local && Number(local.retake_track_submissions) === subCount) {
                        initSigRef.current = extendedSig;
                        stateRef.current = buildInitialTrackingState(null, local);
                        retakeLocalMarkerRef.current = subCount;
                        return;
                    }
                }
            } catch {
            }
        }

        const prevSig = initSigRef.current;
        initSigRef.current = extendedSig;
        const key = getAssignmentTrackingStorageKey(assignmentId, userId, classId, submissionId);
        const keyPending = getAssignmentTrackingStorageKey(assignmentId, userId, classId, null);
        const local = readAssignmentTrackingLocal(key);
        const localPending = readAssignmentTrackingLocal(keyPending);
        const mergedLocal = { ...(localPending || {}), ...(local || {}) };
        let memory = {};
        const prevHead = String(prevSig).split('|')[0];
        if (prevSig && prevHead === String(assignmentId)) {
            memory = { ...stateRef.current };
        }
        const base = buildInitialTrackingState(serverMetaData, mergedLocal);
        stateRef.current = {
            ...base,
            duration_sec: Math.max(base.duration_sec, memory.duration_sec || 0),
            resume_count: Math.max(base.resume_count, memory.resume_count || 0),
            finish_later_count: Math.max(base.finish_later_count, memory.finish_later_count || 0),
            page_visibility_change_count: Math.max(
                base.page_visibility_change_count,
                memory.page_visibility_change_count || 0
            ),
            copy_attempt_count: Math.max(base.copy_attempt_count, memory.copy_attempt_count || 0),
            paste_attempt_count: Math.max(base.paste_attempt_count, memory.paste_attempt_count || 0),
            context_menu_attempt_count: Math.max(
                base.context_menu_attempt_count,
                memory.context_menu_attempt_count || 0
            ),
            retake_sessions_count: Math.max(base.retake_sessions_count, memory.retake_sessions_count || 0),
            window_blur_count: Math.max(base.window_blur_count, memory.window_blur_count || 0),
            copy_content_snippets: mergeSnippetLists(
                memory.copy_content_snippets,
                base.copy_content_snippets
            ),
            paste_content_snippets: mergeSnippetLists(
                memory.paste_content_snippets,
                base.paste_content_snippets
            ),
            assignment_session_started_at:
                memory.assignment_session_started_at || base.assignment_session_started_at,
        };
    }, [
        enabled,
        assignmentId,
        userId,
        classId,
        submissionId,
        serverMetaData,
        isRetakeAttemptInProgress,
        subCount,
        submissionCompleteTruthy,
    ]);

    useEffect(() => {
        if (!enabled || !assignmentId || !userId || !classId || !submissionId) return;
        if (!isRetakeAttemptInProgress) return;
        let retakeSk = '';
        try {
            retakeSk = getRetakeInitSessionStorageKey(assignmentId, submissionId, subCount);
            if (sessionStorage.getItem(retakeSk)) return;
            sessionStorage.setItem(retakeSk, '1');
        } catch {
            return;
        }
        stateRef.current = buildResetTrackingStateForRetakeAttempt(subCount);
        retakeLocalMarkerRef.current = subCount;
        try {
            sessionStorage.removeItem(getResumeOnceSessionStorageKey(assignmentId));
        } catch {
        }
        const key = getAssignmentTrackingStorageKey(assignmentId, userId, classId, submissionId);
        writeAssignmentTrackingLocal(key, {
            duration_sec: 0,
            resume_count: 0,
            finish_later_count: 0,
            page_visibility_change_count: 0,
            copy_attempt_count: 0,
            paste_attempt_count: 0,
            copy_content_snippets: [],
            paste_content_snippets: [],
            context_menu_attempt_count: 0,
            window_blur_count: 0,
            retake_sessions_count: subCount,
            assignment_session_started_at: stateRef.current.assignment_session_started_at,
            retake_track_submissions: subCount,
        });
        schedulePersist();
    }, [
        enabled,
        assignmentId,
        userId,
        classId,
        submissionId,
        isRetakeAttemptInProgress,
        subCount,
        schedulePersist,
    ]);

    useEffect(() => {
        if (!enabled || !assignmentId || !resumeContext || trackingTerminated) return;
        try {
            const sk = getResumeOnceSessionStorageKey(assignmentId);
            if (sessionStorage.getItem(sk)) return;
            sessionStorage.setItem(sk, '1');
            stateRef.current.resume_count += 1;
            persistPartial();
            schedulePersist();
        } catch {
        }
    }, [enabled, assignmentId, resumeContext, trackingTerminated, schedulePersist, persistPartial]);

    useEffect(() => {
        if (!enabled || !assignmentId || !userId || !classId || trackingTerminated) return;
        const id = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            stateRef.current.duration_sec += 1;
            schedulePersist();
        }, 1000);
        return () => clearInterval(id);
    }, [enabled, assignmentId, userId, classId, submissionId, trackingTerminated, schedulePersist]);

    useEffect(() => {
        if (!enabled || !submissionId || trackingTerminated) return;
        const intervalId = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            flushToServer();
        }, ASSIGNMENT_TRACKING_SYNC_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [enabled, submissionId, trackingTerminated, flushToServer]);

    useEffect(() => {
        if (!enabled || !submissionId || trackingTerminated) return;
        const onPageHide = () => {
            persistPartial();
            flushToServer();
        };
        window.addEventListener('pagehide', onPageHide);
        return () => window.removeEventListener('pagehide', onPageHide);
    }, [enabled, submissionId, trackingTerminated, flushToServer, persistPartial]);

    useEffect(() => {
        if (!enabled || trackingTerminated) return;
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                stateRef.current.page_visibility_change_count += 1;
                schedulePersist();
                flushToServer();
            }
        };
        const onPopState = () => {
            stateRef.current.page_visibility_change_count += 1;
            schedulePersist();
        };
        const onHashChange = () => {
            stateRef.current.page_visibility_change_count += 1;
            schedulePersist();
        };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('popstate', onPopState);
        window.addEventListener('hashchange', onHashChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('popstate', onPopState);
            window.removeEventListener('hashchange', onHashChange);
        };
    }, [enabled, trackingTerminated, schedulePersist, flushToServer]);

    useEffect(() => {
        if (!enabled || trackingTerminated) return;

        const recordCopyOrCut = (rawText) => {
            stateRef.current.copy_attempt_count += 1;
            const clip = truncateAssignmentTrackText(rawText, ASSIGNMENT_TRACK_CLIP_MAX);
            if (clip) {
                stateRef.current.copy_content_snippets = pushLimitedSnippet(
                    stateRef.current.copy_content_snippets,
                    clip,
                    ASSIGNMENT_TRACK_SNIPPET_CAP
                );
            }
            schedulePersist();
            scheduleFlushToServer();
        };

        const recordPaste = (rawText) => {
            stateRef.current.paste_attempt_count += 1;
            const clip = truncateAssignmentTrackText(rawText, ASSIGNMENT_TRACK_CLIP_MAX);
            if (clip) {
                stateRef.current.paste_content_snippets = pushLimitedSnippet(
                    stateRef.current.paste_content_snippets,
                    clip,
                    ASSIGNMENT_TRACK_SNIPPET_CAP
                );
            }
            schedulePersist();
            scheduleFlushToServer();
        };

        const getSelectionText = () => {
            try {
                return String(window.getSelection?.()?.toString() || '');
            } catch {
                return '';
            }
        };

        const onCopy = () => {
            recordCopyOrCut(getSelectionText());
        };

        const onCut = () => {
            recordCopyOrCut(getSelectionText());
        };

        const onPaste = (e) => {
            const clipText = e?.clipboardData?.getData?.('text/plain') || '';
            recordPaste(clipText);
        };

        document.addEventListener('copy', onCopy, true);
        document.addEventListener('cut', onCut, true);
        document.addEventListener('paste', onPaste, true);

        return () => {
            document.removeEventListener('copy', onCopy, true);
            document.removeEventListener('cut', onCut, true);
            document.removeEventListener('paste', onPaste, true);
        };
    }, [enabled, trackingTerminated, schedulePersist, scheduleFlushToServer]);

    useEffect(() => {
        if (!enabled || trackingTerminated) return;
        const onBlur = () => {
            stateRef.current.window_blur_count += 1;
            schedulePersist();
        };
        const onContextMenu = () => {
            stateRef.current.context_menu_attempt_count += 1;
            schedulePersist();
        };
        window.addEventListener('blur', onBlur);
        document.addEventListener('contextmenu', onContextMenu, true);
        return () => {
            window.removeEventListener('blur', onBlur);
            document.removeEventListener('contextmenu', onContextMenu, true);
        };
    }, [enabled, trackingTerminated, schedulePersist]);

    useEffect(() => {
        if (!enabled) return;
        return () => {
            if (flushThrottleRef.current) {
                clearTimeout(flushThrottleRef.current);
                flushThrottleRef.current = null;
            }
            persistPartial();
            if (!isTrackingTerminated()) {
                flushToServer({ force: true });
            }
        };
    }, [enabled, persistPartial, flushToServer, isTrackingTerminated]);

    const getMetaData = useCallback(() => {
        return snapshotMetaDataForApi(stateRef.current);
    }, []);

    const recordFinishLater = useCallback(() => {
        try {
            if (assignmentId) sessionStorage.removeItem(getResumeOnceSessionStorageKey(assignmentId));
        } catch {
        }
        stateRef.current.finish_later_count += 1;
        schedulePersist();
        return flushToServer({ force: true }).finally(() => {
            internalSessionEndedRef.current = true;
        });
    }, [assignmentId, schedulePersist, flushToServer]);

    return { getMetaData, recordFinishLater, flushToServer, scheduleFlushToServer };
}
